import React from 'react'
import { Translation } from 'react-i18next'
import { withStyles } from '@material-ui/core'
import classNames from 'classnames'
import MenuItem from '@material-ui/core/MenuItem'
import Button from '@material-ui/core/Button'
import { useForm, Form, Field, useWatch } from '../../../form'
import MultipleSelectionField from '../../../utils/employer/multipleSelectionField.utils'
import AreaOfWorkSelectField from '../../../utils/employer/areaOfWorkSelectField.utils'
import { assignmentValidate } from '../../../containers/employer/validate'
import { Controller } from 'react-hook-form'
import RichTextEditorComponent from '../lightEntrepreneurs/richTextEditor.component'
import { setAssignmentFormReadOnly } from '../../../actions/employer'
import { useDispatch, useSelector } from 'react-redux'

const styles = (theme) => ({
  root: {
    width: '100%',
    overflowX: 'auto',
    '&::-webkit-scrollbar': {
      width: '12px !important',
      webkitOverflowScrolling: 'auto !important'
    },

    '&::-webkit-scrollbar-track': {
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.3) !important',
      webkitBorderRadius: '10px !important',
      borderRadius: '10px !important'
    },

    '&::-webkit-scrollbar-thumb': {
      webkitBorderRadius: '10px !important',
      borderRadius: '10px !important',
      background: '#41617D !important',
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.5) !important'
    },

    '&::-webkit-scrollbar-thumb:window-inactive': {
      background: '#41617D !important'
    }
  },
  tablecell: {
    fontWeight: 'bold',
    fontSize: '13px',
    color: 'black'
  },
  textField: {
    marginTop: theme.spacing(4),
    border: '1px solid gray'
  },
  resize: {
    fontSize: '14px'
  },
  select: {
    paddingRight: '0 !important',
    lineHeight: '1',
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer',
    border: '1px solid gray'
  },
  updateColor: {
    border: '1px solid lightgray'
  },
  button: {
    marginRight: '15px',
    marginTop: '10px'
  },
  button1: {
    marginTop: '10px'
  },
  checkbox: {
    fontSize: '14px',
    fontWeight: '500',
    padding: '3px'
  },
  offerTo: {
    display: 'flex',
    [theme.breakpoints.down('500')]: {
      display: 'block'
    }
  },
  btn: {
    fontWeight: 300,
    fontSize: '14px',
    backgroundColor: 'white',
    border: '1px solid gray',
    borderRadius: 0,
    padding: '4px',
    '&:hover': {
      backgroundColor: '#eeeeee'
    }
  }
})

const today = new Date()

const defaultValues = {
  jobTitle: '',
  assignmentType: 'until_further_notice',
  salaryModel: 'hourly_rate',
  lastDate: today,
  assignmentBegin: today,
  location: '',
  weekdays: false,
  morning: false,
  evening: false,
  night: false,
  weekends: false,
  remote: false,
  undefined_hours: false,
  hourlyRate: '', // payment amount
  weeklyHours: '',
  amount_e: '', // payment amount
  // When assignmentType is until_further_notice or until_completion assignmentEnds is null
  // Keep assignmentEnds is null as default value to prevent sent current date to back-end
  assignmentEnds: null,
  manualNotice: '0',
  contractSalary: '', // payment amount
  unitPrice: '', // payment amount
  job_des: '',
  offer_to_e_email: [],
  offer_to_industry: []
}

const AssignmentForm = ({
  classes,
  isEdit,
  areaOfWork,
  emailArray,
  enableSaveButton,
  // closeAssignmentFormDialog,
  // cancelEditAssignment,
  previewNewAssignment,
  addNewAssignment,
  saveAssignmentAsDraft,
  formPlaced,
  offerFromLightEntre,
  offerToEntrepreneur,
  showAskOfferContract,
  setShowAskOfferContract,
  setAssignmentFormValues
}) => {
  const form = useForm({
    name: 'assignments',
    onSubmit: onSubmit,
    defaultValues: defaultValues,
    resetOnSubmit: false,
    validator: assignmentValidate
  })

  const [isDraft, setIsDraft] = React.useState(false)
  const [isSave, setIsSave] = React.useState(false)
  const [isFormEdited, setIsFormEdit] = React.useState(false)
  const dispatch = useDispatch()
  const isReadOnly = useSelector((state) => state.assignments.isReadOnly)
  const isFinnishLang = useSelector((state) => state.language?.lang) === 'fi'
  const isEstonianLang = useSelector((state) => state.language?.lang) === 'est'

  const {
    control,
    setValue,
    clearErrors,
    formState: { errors }
  } = form

  function onSubmit(values) {
    if (isDraft) {
      saveAssignmentAsDraft(values)
    } else if (isSave) {
      addNewAssignment(values, false)
    } else if (!showAskOfferContract) {
      previewNewAssignment(values)
    }
    setAssignmentFormValues(values) // Save form data
  }

  const watch = useWatch({
    fields: ['assignmentType', 'salaryModel', 'offer_to_e_email'],
    control: control
  })

  const resetFieldValue = () => {
    setValue('hourlyRate', '')
    setValue('weeklyHours', '')
    setValue('amount_e', '')
    setValue('contractSalary', '')
    setValue('unitPrice', '')
    setValue('assignmentEnds', null)
  }

  // React.useEffect(() => {
  //   if (watch.offer_to_e_email.length === 0) {
  //     setValue('offer_to_e_email', [])
  //   }
  // }, [watch])

  const isDisableIndustry =
    isReadOnly ||
    offerFromLightEntre ||
    watch.offer_to_e_email.length > 1 ||
    (watch.offer_to_e_email.length === 1 && watch.offer_to_e_email[0] !== '')
      ? true
      : false

  React.useEffect(() => {
    if (!isEdit && formPlaced === '/dashboard/assignments') {
      dispatch(setAssignmentFormReadOnly(false))
    }
  }, [isEdit, formPlaced])

  React.useEffect(() => {
    if (offerFromLightEntre) {
      setValue('offer_to_e_email', [offerToEntrepreneur?.email])
    }
  }, [offerFromLightEntre, offerToEntrepreneur])

  return (
    <Translation>
      {(t) => {
        return (
          <>
            <div className="row" id="assignment-scroll-section">
              <div className="dashboard-content-header">
                {isReadOnly ? (
                  <h1>{t('employer.assignments.h1d')}</h1>
                ) : isEdit ? (
                  <h1>{t('employer.assignments.h1c')}</h1>
                ) : (
                  <h1>{t('employer.assignments.h1b')}</h1>
                )}
              </div>
            </div>
            <div className="row">
              <div className="dashboard-content-header">
                <div className="col-xs-12 col-sm-12 col-lg-12">
                  <div className="panel panel-default">
                    <div className="panel-body pd-0">
                      <Form {...form}>
                        <div style={{ padding: '15px' }}>
                          <div className="row">
                            <div className="col-xs-12 col-sm-12 col-lg-8">
                              <MultipleSelectionField
                                label={t('employer.assignments.label12')}
                                control={control}
                                name="offer_to_e_email"
                                emails={emailArray}
                                className={classes.select}
                                t={t}
                                setValue={setValue}
                                errors={errors}
                                clearErrors={clearErrors}
                                setIsFormEdit={setIsFormEdit}
                                disabled={isReadOnly || offerFromLightEntre}
                              />
                            </div>
                            <div className="col-xs-12 col-sm-12 col-lg-4">
                              <Field
                                label={t('employer.assignments.label5')}
                                name="location"
                                inputType="autocomplete-places"
                                className={classNames(
                                  classes.textField,
                                  classes.resize
                                )}
                                control={control}
                                onChange={() => {
                                  setIsFormEdit(true)
                                }}
                                searchOptions
                                disabled={isReadOnly}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-xs-12 col-sm-6 col-lg-4">
                              <Field
                                label={t('employer.assignments.label3')}
                                name="jobTitle"
                                inputType="textfield"
                                className={classNames(
                                  classes.textField,
                                  classes.resize
                                )}
                                control={control}
                                onChange={() => {
                                  setIsFormEdit(true)
                                }}
                                helperTextStyle={{
                                  marginTop: '0px',
                                  height: '0px'
                                }}
                                disabled={isReadOnly}
                              />
                              <Field
                                label={t('employer.assignments.label6')}
                                name="assignmentType"
                                inputType="select"
                                className={classNames(classes.select)}
                                control={control}
                                onChange={() => {
                                  setIsFormEdit(true)
                                  resetFieldValue()
                                  clearErrors(['weeklyHours'])
                                }}
                                disabled={isReadOnly}
                              >
                                <MenuItem value="until_further_notice">
                                  {t('employer.assignments.col4a')}
                                </MenuItem>
                                <MenuItem value="temporary">
                                  {t('employer.assignments.col4b')}
                                </MenuItem>
                                <MenuItem value="until_completion">
                                  {t('employer.assignments.col4d')}
                                </MenuItem>
                              </Field>
                              <Field
                                label={t('employer.assignments.label9')}
                                name="salaryModel"
                                inputType="select"
                                className={classNames(classes.select)}
                                control={control}
                                onChange={() => {
                                  setIsFormEdit(true)
                                  resetFieldValue()
                                  clearErrors(['weeklyHours'])
                                }}
                                disabled={isReadOnly}
                              >
                                <MenuItem value="hourly_rate">
                                  {t('employer.lightEntrepreneurs.menuItem1')}
                                </MenuItem>
                                <MenuItem value="contract_price">
                                  {t('employer.lightEntrepreneurs.menuItem2')}
                                </MenuItem>
                                <MenuItem value="unit_price">
                                  {t('employer.lightEntrepreneurs.menuItem3')}
                                </MenuItem>
                                <MenuItem value="commission">
                                  {t('employer.lightEntrepreneurs.menuItem4')}
                                </MenuItem>
                              </Field>
                            </div>
                            <div className="col-xs-12 col-sm-6 col-lg-4">
                              <AreaOfWorkSelectField
                                label={t('employer.assignments.label4')}
                                control={control} // This is the control object from react-hook-form
                                name="offer_to_industry"
                                className={
                                  isDisableIndustry
                                    ? classNames(
                                        classes.select,
                                        classes.updateColor
                                      )
                                    : classNames(classes.select)
                                }
                                options={areaOfWork}
                                t={t}
                                setValue={setValue}
                                clearErrors={clearErrors}
                                setIsFormEdit={setIsFormEdit}
                                disabled={isDisableIndustry}
                              />
                              <div style={{ display: 'flex', gap: '15px' }}>
                                <>
                                  <Field
                                    label={t('employer.assignments.label10')}
                                    name="assignmentBegin"
                                    inputType="datepicker"
                                    className={classNames(
                                      classes.textField,
                                      classes.resize
                                    )}
                                    control={control}
                                    onChange={() => {
                                      setIsFormEdit(true)
                                    }}
                                    minDate={today}
                                    labelStyle={
                                      isFinnishLang
                                        ? {
                                            marginTop: '-12px'
                                          }
                                        : null
                                    }
                                    disabled={isReadOnly}
                                  />
                                  <Field
                                    label={t('employer.assignments.label17')}
                                    name="assignmentEnds"
                                    inputType="datepicker"
                                    className={classNames(
                                      classes.textField,
                                      classes.resize
                                    )}
                                    control={control}
                                    onChange={() => {
                                      setIsFormEdit(true)
                                    }}
                                    labelStyle={
                                      isFinnishLang
                                        ? {
                                            marginTop: '-12px'
                                          }
                                        : null
                                    }
                                    disabled={
                                      isReadOnly ||
                                      !(watch.assignmentType === 'temporary')
                                    }
                                    showPlaceholder={true}
                                  />
                                </>
                              </div>
                              <div style={{ display: 'flex', gap: '15px' }}>
                                <>
                                  <Field
                                    label={t('employer.assignments.label7')}
                                    name="lastDate"
                                    inputType="datepicker"
                                    className={classNames(
                                      classes.textField,
                                      classes.resize
                                    )}
                                    control={control}
                                    onChange={() => {
                                      setIsFormEdit(true)
                                    }}
                                    minDate={today}
                                    labelStyle={
                                      isEstonianLang
                                        ? { marginTop: '-24px' }
                                        : { marginTop: '-12px' }
                                    }
                                    disabled={isReadOnly}
                                  />
                                  <Field
                                    label={t('employer.assignments.label19')}
                                    name="manualNotice"
                                    inputType="textfield"
                                    type="number"
                                    inputMax="185"
                                    inputMin="0"
                                    className={classNames(
                                      classes.textField,
                                      classes.resize
                                    )}
                                    control={control}
                                    onChange={() => {
                                      setIsFormEdit(true)
                                    }}
                                    labelStyle={
                                      !isFinnishLang && { marginTop: '12px' }
                                    }
                                    disabled={isReadOnly}
                                  />
                                </>
                              </div>
                            </div>
                            <div className="col-xs-12 col-sm-6 col-lg-4">
                              <div style={{ marginTop: '24px' }}>
                                <p>{t('employer.assignments.label8')}</p>
                              </div>
                              <div style={{ display: 'flex' }}>
                                <div>
                                  <Field
                                    label={t('employer.assignments.label8a')}
                                    name="weekdays"
                                    inputType="checkbox"
                                    className={classNames(classes.checkbox)}
                                    formControlLabelStyle={{ marginLeft: 0 }}
                                    control={control}
                                    onChange={() => {
                                      setIsFormEdit(true)
                                    }}
                                    disabled={isReadOnly}
                                  />
                                  <Field
                                    label={t('employer.assignments.label8c')}
                                    name="morning"
                                    inputType="checkbox"
                                    className={classNames(classes.checkbox)}
                                    formControlLabelStyle={{ marginLeft: 0 }}
                                    control={control}
                                    onChange={() => {
                                      setIsFormEdit(true)
                                    }}
                                    disabled={isReadOnly}
                                  />
                                  <Field
                                    label={t('employer.assignments.label8e')}
                                    name="evening"
                                    inputType="checkbox"
                                    className={classNames(classes.checkbox)}
                                    formControlLabelStyle={{ marginLeft: 0 }}
                                    control={control}
                                    onChange={() => {
                                      setIsFormEdit(true)
                                    }}
                                    disabled={isReadOnly}
                                  />
                                  <Field
                                    label={t('employer.assignments.label8g')}
                                    name="undefined_hours"
                                    inputType="checkbox"
                                    className={classNames(classes.checkbox)}
                                    formControlLabelStyle={{ marginLeft: 0 }}
                                    control={control}
                                    onChange={() => {
                                      setIsFormEdit(true)
                                    }}
                                    disabled={isReadOnly}
                                  />
                                </div>
                                <div>
                                  <Field
                                    label={t('employer.assignments.label8b')}
                                    name="night"
                                    inputType="checkbox"
                                    className={classNames(classes.checkbox)}
                                    formControlLabelStyle={{ marginLeft: 0 }}
                                    control={control}
                                    onChange={() => {
                                      setIsFormEdit(true)
                                    }}
                                    disabled={isReadOnly}
                                  />
                                  <Field
                                    label={t('employer.assignments.label8d')}
                                    name="weekends"
                                    inputType="checkbox"
                                    className={classNames(classes.checkbox)}
                                    formControlLabelStyle={{ marginLeft: 0 }}
                                    control={control}
                                    onChange={() => {
                                      setIsFormEdit(true)
                                    }}
                                    disabled={isReadOnly}
                                  />
                                  <Field
                                    label={t('employer.assignments.label8f')}
                                    name="remote"
                                    inputType="checkbox"
                                    className={classNames(classes.checkbox)}
                                    formControlLabelStyle={{ marginLeft: 0 }}
                                    control={control}
                                    onChange={() => {
                                      setIsFormEdit(true)
                                    }}
                                    disabled={isReadOnly}
                                  />
                                </div>
                              </div>
                              <div
                                className="col-xs-12 col-sm-6 col-lg-6"
                                style={{ paddingLeft: 0 }}
                              >
                                {watch.assignmentType && watch.salaryModel ? (
                                  (watch.assignmentType !== '' &&
                                    watch.salaryModel === 'hourly_rate') ||
                                  (watch.assignmentType ===
                                    'until_completion' &&
                                    watch.salaryModel === 'commission') ? (
                                    <Field
                                      label={t('employer.assignments.label13')}
                                      name="hourlyRate"
                                      inputType="textfield"
                                      className={classNames(
                                        classes.textField,
                                        classes.resize
                                      )}
                                      control={control}
                                      onChange={() => setIsFormEdit(true)}
                                      disabled={isReadOnly}
                                    />
                                  ) : watch.assignmentType ===
                                      'until_completion' &&
                                    watch.salaryModel === 'unit_price' ? (
                                    <Field
                                      label={t('employer.assignments.label21')}
                                      name="unitPrice"
                                      inputType="textfield"
                                      className={classNames(
                                        classes.textField,
                                        classes.resize
                                      )}
                                      control={control}
                                      onChange={() => setIsFormEdit(true)}
                                      disabled={isReadOnly}
                                    />
                                  ) : watch.assignmentType ===
                                      'until_completion' &&
                                    watch.salaryModel === 'contract_price' ? (
                                    <Field
                                      label={t('employer.assignments.label20')}
                                      name="contractSalary"
                                      inputType="textfield"
                                      className={classNames(
                                        classes.textField,
                                        classes.resize
                                      )}
                                      control={control}
                                      onChange={() => setIsFormEdit(true)}
                                      disabled={isReadOnly}
                                    />
                                  ) : (
                                    <Field
                                      label={
                                        ([
                                          'until_further_notice',
                                          'temporary'
                                        ].includes(watch.assignmentType) &&
                                          watch.salaryModel ===
                                            'contract_price') ||
                                        (watch.assignmentType === 'temporary' &&
                                          watch.salaryModel === 'commission')
                                          ? t('employer.assignments.label15')
                                          : [
                                              'until_further_notice',
                                              'temporary'
                                            ].includes(watch.assignmentType) &&
                                            watch.salaryModel === 'unit_price'
                                          ? t('employer.assignments.label18')
                                          : watch.assignmentType ===
                                              'until_further_notice' &&
                                            watch.salaryModel === 'commission'
                                          ? t('employer.assignments.label20')
                                          : ''
                                      }
                                      name="amount_e"
                                      inputType="textfield"
                                      className={classNames(
                                        classes.textField,
                                        classes.resize
                                      )}
                                      control={control}
                                      onChange={() => setIsFormEdit(true)}
                                      disabled={isReadOnly}
                                    />
                                  )
                                ) : null}
                              </div>
                              <div
                                className="col-xs-12 col-sm-6 col-lg-6"
                                style={{ paddingLeft: 0 }}
                              >
                                {watch.assignmentType && watch.salaryModel ? (
                                  <Field
                                    label={
                                      !(
                                        watch.assignmentType !== '' &&
                                        (watch.salaryModel === 'hourly_rate' ||
                                          watch.salaryModel === 'commission')
                                      )
                                        ? t('employer.assignments.label14')
                                        : t('employer.assignments.label14') +
                                          '*'
                                    }
                                    name="weeklyHours"
                                    inputType="textfield"
                                    type="number"
                                    inputMax="70"
                                    inputMin="0"
                                    className={classNames(
                                      classes.textField,
                                      classes.resize
                                    )}
                                    control={control}
                                    onChange={() => setIsFormEdit(true)}
                                    disabled={
                                      isReadOnly ||
                                      !(
                                        watch.assignmentType !== '' &&
                                        (watch.salaryModel === 'hourly_rate' ||
                                          watch.salaryModel === 'commission')
                                      )
                                    }
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            <div
                              className="col-xs-12 col-sm-12 col-lg-12"
                              style={{ padding: '15px' }}
                            >
                              <span>{t('employer.assignments.label11')}</span>
                              <div style={{ paddingTop: '5px' }}>
                                <Controller
                                  render={({ field: { value, onChange } }) => {
                                    const handleEditorChange = (
                                      editorValue
                                    ) => {
                                      onChange(editorValue)
                                      setIsFormEdit(true)
                                    }
                                    return (
                                      <RichTextEditorComponent
                                        value={value}
                                        onEditorChange={handleEditorChange}
                                        disabled={isReadOnly}
                                      />
                                    )
                                  }}
                                  name="job_des"
                                  control={control}
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="col-xs-12 col-sm-12 col-lg-12 text-right"
                            style={{
                              margin: '25px 0px 5px 0px',
                              paddingRight: 0
                            }}
                          >
                            {isEdit ? (
                              <>
                                {/* <Button
                                  variant="outlined"
                                  className={classNames(classes.button)}
                                  onClick={cancelEditAssignment}
                                >
                                  {t('employer.assignments.btn4')}
                                </Button> */}
                                <Button
                                  variant="outlined"
                                  className={classNames(classes.button)}
                                  type="submit"
                                  onClick={() => {
                                    setIsDraft(true)
                                    setIsSave(false)
                                  }}
                                >
                                  {t('employer.assignments.btn1')}
                                </Button>
                                <Button
                                  variant="contained"
                                  className={classNames(classes.button)}
                                  type="submit"
                                  onClick={() => {
                                    setIsDraft(false)
                                    setIsSave(false)
                                    setIsFormEdit(false)
                                  }}
                                >
                                  {t('employer.assignments.btn2')}
                                </Button>
                                <Button
                                  variant="contained"
                                  className={classNames(classes.button1)}
                                  type="submit"
                                  disabled={
                                    (enableSaveButton ? false : true) ||
                                    isFormEdited ||
                                    Object.keys(errors).length > 0
                                  }
                                  onClick={() => {
                                    setIsSave(true)
                                    setIsDraft(false)
                                  }}
                                >
                                  {t('employer.assignments.btn3')}
                                </Button>
                              </>
                            ) : (
                              <>
                                {/* <Button
                                  variant="outlined"
                                  className={classNames(classes.button)}
                                  onClick={closeAssignmentFormDialog}
                                >
                                  {t('employer.assignments.close')}
                                </Button> */}
                                {!offerFromLightEntre && (
                                  <Button
                                    variant="outlined"
                                    className={classNames(classes.button)}
                                    type="submit"
                                    onClick={() => {
                                      setIsDraft(true)
                                      setIsSave(false)
                                    }}
                                  >
                                    {t('employer.assignments.btn1')}
                                  </Button>
                                )}
                                <Button
                                  variant="contained"
                                  className={classNames(classes.button)}
                                  type="submit"
                                  onClick={() => {
                                    setIsDraft(false)
                                    setIsSave(false)
                                    setIsFormEdit(false)
                                  }}
                                >
                                  {t('employer.assignments.btn2')}
                                </Button>
                                <Button
                                  variant="contained"
                                  className={classNames(classes.button1)}
                                  type="submit"
                                  disabled={
                                    (enableSaveButton ? false : true) ||
                                    isFormEdited ||
                                    Object.keys(errors).length > 0
                                  }
                                  onClick={() => {
                                    if (offerFromLightEntre) {
                                      setShowAskOfferContract(true)
                                    } else {
                                      setIsSave(true)
                                    }
                                    setIsDraft(false)
                                  }}
                                >
                                  {t('employer.assignments.btn3')}
                                </Button>
                              </>
                            )}
                          </div>
                          <div className="pull-right">
                            <p>{t('employer.assignments.p')}</p>
                          </div>
                        </div>
                      </Form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )
      }}
    </Translation>
  )
}

export default withStyles(styles)(AssignmentForm)
